import { Store } from '@ngrx/store';
import { Injectable, inject } from '@angular/core';
import { Observable, from, map, switchMap, combineLatest, take } from 'rxjs';
import { Auth, user, idToken, signInWithEmailAndPassword, sendPasswordResetEmail, getAuth, confirmPasswordReset, checkActionCode } from '@angular/fire/auth';
import { HttpClient, HttpContext } from '@angular/common/http';
import { IAuthenticationLoginRequest, IAuthenticationPasswordRequest, VerifyEmailResponse } from 'src/app/core/interfaces/authentication';
import { IUser } from '../../models/user.model';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { cleantAuthState, setUser } from '../../store/auth/auth.action';
import { getAuthStateSelect } from '../../store/auth/auth.selector';
import { AuthenticateReturn } from './authenticate.model';
import { APIResponse } from '../../http/http.model';
import { CALL_LOADING } from '../../loading-interceptor/loading-interceptor.service';
import { MatDialog } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { ScheduleService } from 'src/app/features/schedule/services/schedule.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private afAuth: Auth = inject(Auth);
  private httpClient = inject(HttpClient);
  private store = inject(Store);
  private localStorageService = inject(LocalStorageService)

  constructor(
    private dialogRef: MatDialog,
    private viewScheduleService: ScheduleService,
  ) { }

  context = new HttpContext().set(CALL_LOADING, 'general')


  login({ email, password }: IAuthenticationLoginRequest): Observable<any> {
    return from(signInWithEmailAndPassword(this.afAuth, email, password)).pipe(
      switchMap((u) => this.getUserData())
    )
  }

  authenticated(): Observable<boolean> {
    return combineLatest([
      user(this.afAuth),
      this.getAuthState()
    ]).pipe(
      map(([firebaseUser, storeUser]) => {
        const localStorageUser = this.localStorageService.get('user');
        if (firebaseUser && localStorageUser) {
          this.store.dispatch(setUser({ user: localStorageUser }));
          return true;
        }

        return false;
      })
    )

  }

  getAuthState(): Observable<IUser | null> {
    return this.store.select(getAuthStateSelect).pipe(
      map((state) => {
        return state?.user ?? null;
      })
    )
  }

  updatePassword({ oobCode, password }: IAuthenticationPasswordRequest): Observable<any> {
    return from(confirmPasswordReset(getAuth(), oobCode, password));
  }

  sendEmailResetPassword(email: string): Observable<void> {
    return from(sendPasswordResetEmail(getAuth(), email));
  }

  updateFirstAccess(email: string): Observable<any> {
    return this.httpClient.put<any>(`${environment.patientUrl}V2/admin/${environment.datastore}updateFirstAccess?email=${email}`, { context: this.context }).pipe(
      take(1)
    );
  }

  checkActionCode(oobCode: string): Observable<any> {
    return from(checkActionCode(getAuth(), oobCode));
  }

  getToken(): Observable<string | null> {
    return idToken(this.afAuth);
  }

  logoutMethod(email: string, type: string): Observable<any> {
    return this.httpClient.post<any>(`${environment.patientUrl}V2/${environment.datastore}logout?email=${email}&type=${type}`, { context: this.context }).pipe(
      take(1)
    );
  }

  getUserData(): Observable<any> {
    return combineLatest([
      user(this.afAuth),
      this.httpClient.get<APIResponse<AuthenticateReturn>>(`${environment.api.baseUrl}V2/get-user-details?lg=pt-BR&isOldClinic=false`)
    ]).pipe(
      map(([firebaseUser, response]) => {
        if (response.status === 'notok') {
          throw new Error()
        }
        const user: IUser & { clinic: AuthenticateReturn['clinic'] } = {
          uid: response.result.uid,
          name: response.result.name,
          email: response.result.email,
          firstAccess: response.result.firstAccess,
          practitionerId: response.result.practitionerId,
          userProfile: response.result.userProfile,
          permission: response.result.permission,
          clinic: response.result.clinic,
          specialty: response.result.specialty,
          lastLogout: response.result.lastLogout,
          lastSignInTime: firebaseUser?.metadata.lastSignInTime,
          medicalCouncil: response.result.conselhoType,
          medicalCouncilNumber: response.result.conselhoNumber
        };
        this.localStorageService.set('user', user);
        return user;
      })
    );
  }

  logout(): void {
    this.dialogRef.closeAll();
    Swal.close();
    localStorage.setItem('lastPatient', 'null');

    this.viewScheduleService.registrationForm.controls.unitySelected.setValue('Todas unidades', { emitEvent: false });
    this.viewScheduleService.registrationForm.controls.patientSelected.setValue('Todos pacientes', { emitEvent: false });
    this.viewScheduleService.registrationForm.controls.typeSelected.setValue('Todos tipos', { emitEvent: false });

    this.afAuth.signOut();
    this.localStorageService.clear();
    this.store.dispatch(cleantAuthState());
  }
}
